import { useCallback, useState } from 'react'
import { Linking, Platform } from 'react-native'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { resolveLanguage, useLanguage } from '@context'
import {
  appendAvatarToFormData,
  blobToBase64,
  getMimeTypeAndExtension,
  isNetworkError,
  shareFile,
  showApiErrorToast,
  showSuccessToast,
  type EventPayload,
} from '@utils'
import { secureTokenStore, useCurrentUserStore, useLanguageStore } from '@store'
import { EVENTS_QUERY_KEY, checkInLiveKey } from '../../queries/events/useEventQueries'
import { getOfflineDeviceId } from '../../../../screens/menu/events/offlineCheckIn/offlineCheckInStore'
import { navigationRef } from '../../../../navigationRef'
import { useApiErrorHandler } from '../../../useApiErrorHandler'
import type {
  CheckoutResponse,
  EventPayment,
  KtEvent,
  OrganizerAttendee,
  ScanCheckInResult,
  SendEventEmailInput,
  SendEventEmailResult,
  SendEventTicketsInput,
  SendEventTicketsResult,
  SimulatedPaymentResponse,
} from '../../queries/events/types'

/** `{ account, accessToken, refreshToken }` — same envelope as /authentication/sign-in. */
export interface EventSession {
  account: { id: string; email: string; isOnboardingCompleted: boolean }
  accessToken: string
  refreshToken: string
  attendance?: {
    attendeeId: string | null
    alreadyRegistered: boolean
    /** Paid event, no invitation: the person must now pay (start a checkout). */
    paymentRequired?: boolean
  }
}

/**
 * Logs the user in from an event sign-up (email code or LinkedIn): stores the
 * tokens, then flips `userID`, which is what the whole app treats as
 * "logged in" (MainNavigator + WebLayout react to it).
 */
export const useCompleteEventSignIn = () => {
  const queryClient = useQueryClient()
  const setUserID = useCurrentUserStore((state) => state.setUserID)
  return useCallback(
    async (session: EventSession) => {
      await secureTokenStore.setTokens(session.accessToken, session.refreshToken)
      setUserID(session.account.id)
      await queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
      void queryClient.invalidateQueries({ queryKey: ['subscription'] })
    },
    [queryClient, setUserID],
  )
}

const useInvalidateEvents = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
}

/* ---------- Sign-up / sign-in (public) ---------- */

export const useRequestEmailCode = () =>
  useMutation<
    { sent: boolean },
    Error,
    { email: string; purpose: 'join' | 'sign-in'; eventCode?: string }
  >({
    mutationFn: async (body) =>
      (await api.post<{ sent: boolean }>('/public/email-code', body)).data,
  })

// The language the sign-up is read in, sent like the ordinary sign-up does: an
// account created by the join starts in it. It used to start in French, and
// the whole app turned French on an English or Spanish device once joined.
const languageOnScreen = () => resolveLanguage(useLanguageStore.getState().language)

export interface JoinWithEmailInput {
  eventCode: string
  email: string
  code: string
  firstName: string
  lastName: string
  jobTitle: string
  company?: string
  linkedinUrl?: string
}

export const useJoinEventWithEmail = () =>
  useMutation<EventSession, Error, JoinWithEmailInput>({
    mutationFn: async ({ eventCode, ...body }) =>
      (
        await api.post<EventSession>(`/public/events/${eventCode}/join/email`, {
          ...body,
          language: languageOnScreen(),
        })
      ).data,
  })

export const useJoinEventWithLinkedIn = () =>
  useMutation<
    EventSession,
    Error,
    { eventCode: string; authorizationCode: string; redirectUri: string }
  >({
    mutationFn: async ({ eventCode, ...body }) =>
      (
        await api.post<EventSession>(`/public/events/${eventCode}/join/linkedin`, {
          ...body,
          language: languageOnScreen(),
        })
      ).data,
  })

export const useSignInWithEmailCode = () =>
  useMutation<EventSession, Error, { email: string; code: string }>({
    mutationFn: async (body) =>
      (await api.post<EventSession>('/authentication/email-code/sign-in', body)).data,
  })

/* ---------- Attendee (logged in) ---------- */

export const useJoinEvent = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<{ attendance: NonNullable<EventSession['attendance']> }, Error, string>({
    mutationFn: async (eventCode) =>
      (
        await api.post<{ attendance: NonNullable<EventSession['attendance']> }>(
          `/events/by-code/${eventCode}/join`,
        )
      ).data,
    onSuccess: async ({ attendance }) => {
      // Paid event: nothing is registered yet, the caller starts the payment.
      if (attendance.paymentRequired) return
      await invalidate()
      showSuccessToast(t('events.toast.joinedTitle'), t('events.toast.joinedMessage'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/* ---------- Payment (paid events) ---------- */

/** Holds a seat for 15 minutes and returns the payment page (idempotent). */
export const useEventCheckout = () =>
  useMutation<CheckoutResponse, Error, string>({
    mutationFn: async (eventId) =>
      (await api.post<CheckoutResponse>(`/events/${eventId}/checkout`)).data,
  })

/** The payment page of the simulated provider lives inside the app. */
const SIMULATED_PATH = /\/pay\/simulated\/([0-9a-f-]{36})/i

/**
 * Starts the payment of a paid event and sends the person to the payment page.
 * Provider-agnostic: the app's own simulated screen is opened in-app, any
 * other URL (Stripe's hosted Checkout, later) is opened as a normal link.
 */
export const useStartEventPayment = () => {
  const checkout = useEventCheckout()
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()

  const openPayment = useCallback(
    (payment: EventPayment) => {
      const paymentId = payment.checkoutUrl
        ? SIMULATED_PATH.exec(payment.checkoutUrl)?.[1]
        : undefined
      // The global ref, not a screen's own navigation: the screen that starts a
      // payment (LinkedIn callback, sign-up panel) is often gone by then.
      if (paymentId) navigationRef.navigate('SimulatedPayment', { paymentId })
      else if (payment.checkoutUrl) {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window.location.assign(payment.checkoutUrl)
        } else void Linking.openURL(payment.checkoutUrl)
      }
    },
    [],
  )

  /** `event.id` when known, else the public code (right after a LinkedIn sign-up). */
  const startPayment = useCallback(
    async (event: { id?: string; code?: string }) => {
      try {
        const eventId =
          event.id ??
          (await api.get<{ event: KtEvent }>(`/events/by-code/${event.code}`)).data.event.id
        const result = await checkout.mutateAsync(eventId)
        await invalidate()
        if (result.payment && !result.alreadyRegistered) openPayment(result.payment)
      } catch (error) {
        showApiErrorToast(t, error)
      }
    },
    [checkout, invalidate, openPayment, t],
  )

  return { startPayment, openPayment, isStarting: checkout.isPending }
}

/**
 * The simulated provider's buttons. They call the same route a real
 * provider's webhook will reach, then refresh every event query.
 */
export const useCompleteSimulatedPayment = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<
    Pick<SimulatedPaymentResponse, 'payment'>,
    Error,
    { paymentId: string; outcome: 'paid' | 'failed' }
  >({
    mutationFn: async ({ paymentId, outcome }) =>
      (
        await api.post<Pick<SimulatedPaymentResponse, 'payment'>>(
          `/payments/simulated/${paymentId}/complete`,
          { outcome },
        )
      ).data,
    onSuccess: () => invalidate(),
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useLeaveEvent = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<unknown, Error, string>({
    mutationFn: async (eventId) =>
      (await api.delete<unknown>(`/events/${eventId}/attendance`)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('events.toast.leftTitle'), t('events.toast.leftMessage'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useUpdateEventProfile = () => {
  const invalidate = useInvalidateEvents()
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<unknown, Error, { jobTitle?: string; company?: string; linkedinUrl?: string }>(
    {
      mutationFn: async (body) => (await api.patch<unknown>('/me/event-profile', body)).data,
      onSuccess: async () => {
        await invalidate()
        void queryClient.invalidateQueries({ queryKey: ['profile'] })
        showSuccessToast(t('events.toast.profileSavedTitle'), t('events.toast.profileSavedMessage'))
      },
      onError: (error) => showApiErrorToast(t, error),
    },
  )
}

/* ---------- Organizer ---------- */

export type EventFormValues = EventPayload

export interface UploadCoverInput {
  /** Web: the picked file. */
  file?: Blob
  /** Native: data URI from the image picker. */
  uri?: string
  /** 0–100 while the request body is sent. */
  onProgress?: (percent: number) => void
}

/** Uploads a cover image and resolves with its public URL. */
export const useUploadEventCover = () =>
  useMutation<{ url: string }, Error, UploadCoverInput>({
    mutationFn: async ({ file, uri, onProgress }) => {
      const formData = new FormData()
      if (file) formData.append('file', file, 'cover')
      else if (uri) {
        appendAvatarToFormData(
          formData,
          uri,
          'file',
          `cover.${getMimeTypeAndExtension(uri).extension}`,
        )
      }
      return (
        await api.post<{ url: string }>('/events/cover-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100))
          },
        })
      ).data
    },
  })

export const useCreateEvent = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<{ event: KtEvent }, Error, EventFormValues>({
    mutationFn: async (body) => (await api.post<{ event: KtEvent }>('/events', body)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('events.toast.createdTitle'), t('events.toast.createdMessage'))
    },
  })
}

export const useUpdateEvent = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<
    { event: KtEvent },
    Error,
    { eventId: string; values: Partial<EventFormValues> }
  >({
    mutationFn: async ({ eventId, values }) =>
      (await api.patch<{ event: KtEvent }>(`/events/${eventId}`, values)).data,
    onSuccess: async () => {
      await invalidate()
      showSuccessToast(t('events.toast.updatedTitle'), t('events.toast.updatedMessage'))
    },
  })
}

export const useDeleteEvent = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<unknown, Error, string>({
    mutationFn: async (eventId) => (await api.delete<unknown>(`/events/${eventId}`)).data,
    onSuccess: (_data, eventId) => {
      // Forget everything about the deleted event BEFORE refreshing the rest:
      // refetching it would only bring 404s (and their retries) while the
      // screen waits, and the caller must be able to navigate away at once.
      queryClient.removeQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === EVENTS_QUERY_KEY && queryKey.includes(eventId),
      })
      void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
      showSuccessToast(t('events.toast.deletedTitle'), t('events.toast.deletedMessage'))
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export const useCheckInAttendee = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<unknown, Error, { eventId: string; attendeeId: string; checkedIn: boolean }>({
    mutationFn: async ({ eventId, attendeeId, checkedIn }) =>
      (
        await api.put<unknown>(`/organizer/events/${eventId}/attendees/${attendeeId}/check-in`, {
          checkedIn,
        })
      ).data,
    onSuccess: () => invalidate(),
    onError: (error) => showApiErrorToast(t, error),
  })
}

/**
 * Entrance screen: the tick shows instantly. If the API refuses, that one
 * person goes back to their previous state and an error toast explains why.
 */
export const useLiveCheckIn = (eventId: string) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const key = checkInLiveKey(eventId)
  const setChecked = (attendeeId: string, checkedInAt: string | null) =>
    queryClient.setQueryData<OrganizerAttendee[]>(key, (list) =>
      list?.map((a) => (a.attendeeId === attendeeId ? { ...a, checkedInAt } : a)),
    )
  return useMutation<
    unknown,
    Error,
    { attendeeId: string; checkedIn: boolean },
    { attendeeId: string; previous: string | null }
  >({
    // Same reasoning as useScanCheckIn/useRegisterCheckIn: without this,
    // React Query silently PAUSES the mutation while `navigator.onLine` is
    // false — it never attempts it, never errors, the optimistic tick just
    // hangs forever instead of rolling back with a toast.
    networkMode: 'always',
    mutationFn: async ({ attendeeId, checkedIn }) =>
      (
        await api.put<unknown>(`/organizer/events/${eventId}/attendees/${attendeeId}/check-in`, {
          checkedIn,
        })
      ).data,
    onMutate: async ({ attendeeId, checkedIn }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous =
        queryClient.getQueryData<OrganizerAttendee[]>(key)?.find((a) => a.attendeeId === attendeeId)
          ?.checkedInAt ?? null
      setChecked(attendeeId, checkedIn ? new Date().toISOString() : null)
      return { attendeeId, previous }
    },
    onError: (error, _vars, context) => {
      // Only this person goes back: other ticks made meanwhile stay.
      if (context) setChecked(context.attendeeId, context.previous)
      showApiErrorToast(t, error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
    },
  })
}

/**
 * Entrance scan: reads a ticket QR or a personal KeepTouch QR. Always a 200
 * with a `status` (checked_in, already_checked_in, not_registered, wrong_event,
 * invalid) — never a toast, the scan screen shows its own full-screen result.
 * `deviceId` (one per scanner phone, persisted on the device) is what lets a
 * second scan tell "you" apart from "another device" on a conflict. A network
 * error is never toasted here: the caller falls back to the offline manifest.
 */
export const useScanCheckIn = (eventId: string) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<ScanCheckInResult, Error, string>({
    // React Query's default `networkMode: 'online'` would PAUSE this mutation
    // (never even attempt it, never call onError) the moment the browser
    // reports itself offline — which would silently hang the whole offline
    // fallback below instead of triggering it. 'always' makes it actually
    // try, so a real network error reaches `onError` and the manifest match can run.
    networkMode: 'always',
    mutationFn: async (code) =>
      (
        await api.post<ScanCheckInResult>(`/organizer/events/${eventId}/check-in/scan`, {
          code,
          deviceId: getOfflineDeviceId(),
        })
      ).data,
    onSuccess: (result) => {
      if (result.status === 'checked_in') {
        void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
      }
    },
    onError: (error) => {
      if (isNetworkError(error)) return
      showApiErrorToast(t, error)
    },
  })
}

/**
 * "L'inscrire": registers a scanned-but-unregistered account on the spot and
 * checks it in at once. Same result shape as the scan (plus full / closed /
 * payment_required / invalid for the cases that block the registration). This
 * always needs the network (it creates the seat server-side): no offline path.
 */
export const useRegisterCheckIn = (eventId: string) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<ScanCheckInResult, Error, string>({
    // Same reasoning as `useScanCheckIn`: attempt for real rather than sit
    // paused, so a genuinely offline "L'inscrire" fails with its normal
    // toast instead of hanging silently until the connection returns.
    networkMode: 'always',
    mutationFn: async (accountId) =>
      (
        await api.post<ScanCheckInResult>(`/organizer/events/${eventId}/check-in/register`, {
          accountId,
          deviceId: getOfflineDeviceId(),
        })
      ).data,
    onSuccess: (result) => {
      if (result.status === 'checked_in') {
        void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY] })
      }
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** "Envoyer les billets" from the CRM: to every confirmed attendee, or one from their row. */
export const useSendEventTickets = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<SendEventTicketsResult, Error, SendEventTicketsInput>({
    mutationFn: async ({ eventId, ...body }) =>
      (await api.post<SendEventTicketsResult>(`/organizer/events/${eventId}/tickets/send`, body)).data,
    onSuccess: (_data, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY, 'email-campaigns', eventId] })
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

export interface InviteResult {
  sent: string[]
  alreadyRegistered: string[]
  invalid: string[]
  /** Addresses that used "Ne plus recevoir d'invitations": nothing was sent to them. */
  optedOut?: string[]
}

export const useInviteToEvent = () => {
  const invalidate = useInvalidateEvents()
  const { t } = useLanguage()
  return useMutation<
    InviteResult,
    Error,
    { eventId: string; emails?: string[]; contactAccountIds?: string[] }
  >({
    mutationFn: async ({ eventId, ...body }) =>
      (await api.post<InviteResult>(`/organizer/events/${eventId}/invitations`, body)).data,
    onSuccess: () => invalidate(),
    onError: (error) => showApiErrorToast(t, error),
  })
}

/** "Emailing simple" (+ the "notification seulement" variant), same endpoint. */
export const useSendEventEmail = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  return useMutation<SendEventEmailResult, Error, SendEventEmailInput>({
    mutationFn: async ({ eventId, ...body }) =>
      (await api.post<SendEventEmailResult>(`/organizer/events/${eventId}/emails`, body)).data,
    onSuccess: (_data, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: [EVENTS_QUERY_KEY, 'email-campaigns', eventId] })
    },
    onError: (error) => showApiErrorToast(t, error),
  })
}

/**
 * Downloads the attendee CSV. Fetched as a Blob so the UTF-8 BOM (which makes
 * Excel read the accents right) is kept byte for byte.
 */
export const useExportEventAttendees = () => {
  const { t } = useLanguage()
  const [isExporting, setIsExporting] = useState(false)
  const exportAttendees = useCallback(
    async (eventId: string) => {
      setIsExporting(true)
      try {
        const response = await api.get<Blob>(`/organizer/events/${eventId}/attendees/export`, {
          responseType: 'blob',
        })
        const disposition = String(response.headers['content-disposition'] ?? '')
        const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'participants.csv'
        if (Platform.OS === 'web') {
          const url = URL.createObjectURL(response.data)
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = filename
          document.body.appendChild(anchor)
          anchor.click()
          anchor.remove()
          URL.revokeObjectURL(url)
          showSuccessToast(t('events.toast.exportedTitle'), filename)
        } else {
          // Native: the share sheet ("Save to Files", Mail, AirDrop…) is the download.
          const base64 = await blobToBase64(response.data)
          await shareFile({ fileName: filename, base64, title: filename })
        }
      } catch (error) {
        showApiErrorToast(t, error)
      } finally {
        setIsExporting(false)
      }
    },
    [t],
  )
  return { exportAttendees, isExporting }
}

/**
 * "Ajouter" from an event page: the person goes straight into the contacts
 * (no request, nobody is notified), with the event name as meeting place.
 * A free account at its contact cap gets the Premium paywall.
 */
export const useConnectWithAttendee = (eventId: string | undefined, eventName: string) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const handleApiError = useApiErrorHandler()
  return useMutation<
    { alreadyContact: boolean; mutual: boolean },
    Error,
    { recipientAccountId: string }
  >({
    mutationFn: async (body) =>
      (await api.post<{ alreadyContact: boolean; mutual: boolean }>(`/events/${eventId}/connect`, body))
        .data,
    onSuccess: async () => {
      showSuccessToast(
        t('events.relation.addedTitle'),
        t('events.relation.addedMessage', { event: eventName }),
      )
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contact'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts-suggested'] }),
      ])
    },
    onError: handleApiError,
  })
}
